import type {
  DiagnosticResponses,
  FrameworkRouteCommitRequest,
  FrameworkRoutePreviewRequest,
} from '$/requests/FrameworkRoute.ts';
import type {
  InterpretationConfidenceKey,
  PrimaryConstraintKey,
  PrimaryIssueTypeKey,
} from '@/pages/clarityDiagnostic/signalConfirmationOptions.ts';

/** Stepper context for CLARITY — Figma fields + AI preview/commit artifacts. */
export type ClarityDiagnosticCapture = {
  decision_id?: string;
  primary_issue_type?: PrimaryIssueTypeKey | string;
  primary_constraint?: PrimaryConstraintKey | string;
  constraint_other?: string;
  interpretation_confidence?: InterpretationConfidenceKey | string;
  ambiguity_clarifications?: string[];
  confirmation_sentence?: string;
  is_ambiguous?: boolean;
  ambiguity_question?: string;
  diagnostic_summary?: string;
  assumption_record?: string;
  risk_clarification?: string;
  stakeholder_impact?: string;
  approval_readiness_score?: number;
};

function buildCriticalElement(data: ClarityDiagnosticCapture): string {
  const constraint = data.primary_constraint?.trim() ?? '';
  const other = data.constraint_other?.trim() ?? '';
  const clarifications = (data.ambiguity_clarifications ?? []).map((c) => c.trim()).filter(Boolean);

  let base = '';
  if (constraint === 'other') {
    base = other || 'Other constraint (unspecified)';
  } else if (constraint) {
    base = constraint;
  }

  if (clarifications.length > 0) {
    const refined = clarifications.join(' | ');
    return base ? `${base} — ${refined}` : refined;
  }

  return base;
}

/** Maps Figma signal chips → existing DiagnosticResponses contract (no API schema change). */
function buildDiagnosticResponses(data: ClarityDiagnosticCapture): DiagnosticResponses {
  const strategic_mode = data.primary_issue_type?.trim() ?? '';
  const primary_objective = data.primary_constraint?.trim() ?? '';
  const roi_potential = data.interpretation_confidence?.trim() ?? '';
  const critical_element = buildCriticalElement(data);
  const ambiguity_clarifications = (data.ambiguity_clarifications ?? [])
    .map((c) => c.trim())
    .filter(Boolean);

  if (!strategic_mode || !primary_objective || !roi_potential) {
    throw new Error('Primary issue type, primary constraint, and interpretation confidence are required');
  }
  if (primary_objective === 'other' && !data.constraint_other?.trim()) {
    throw new Error('Please describe the constraint when Other is selected');
  }
  if (!critical_element) {
    throw new Error('Primary constraint is required');
  }

  return {
    strategic_mode,
    primary_objective:
      primary_objective === 'other' ? (data.constraint_other?.trim() ?? 'other') : primary_objective,
    roi_potential,
    critical_element,
    ...(ambiguity_clarifications.length > 0 ? { ambiguity_clarifications } : {}),
  };
}

function requireDecisionId(data: ClarityDiagnosticCapture): string {
  const decision_id = data.decision_id?.trim() ?? '';
  if (!decision_id) {
    throw new Error('Decision session is required');
  }
  return decision_id;
}

export function buildFrameworkRoutePreviewRequest(
  data: ClarityDiagnosticCapture,
): FrameworkRoutePreviewRequest {
  return {
    decision_id: requireDecisionId(data),
    diagnostic_responses: buildDiagnosticResponses(data),
  };
}

export function buildFrameworkRouteCommitRequest(
  data: ClarityDiagnosticCapture,
): FrameworkRouteCommitRequest {
  const confirmation_sentence = data.confirmation_sentence?.trim() ?? '';
  if (!confirmation_sentence) {
    throw new Error('Confirmation sentence is required');
  }

  return {
    decision_id: requireDecisionId(data),
    diagnostic_responses: buildDiagnosticResponses(data),
    confirmation_sentence,
  };
}

/** Figma: Refine the Case when user reports low confidence or AI needs clarification. */
export function shouldEnterAmbiguityState(
  interpretationConfidence: string | undefined,
  isAmbiguous: boolean,
): boolean {
  return interpretationConfidence === 'low' || isAmbiguous;
}